import _get from 'lodash/get';
import _find from 'lodash/find';
import _sortBy from 'lodash/sortBy';
import _last from 'lodash/last';
import _reject from 'lodash/reject';

import paymentProfileService from 'api/paymentProfile';
import SEGMENT_EVENTS from 'constants/segmentEvents';

const PAYMENT_PROVIDER_TYPE = {
  BRAINTREE: 'BRAINTREE',
  STRIPE: 'STRIPE'
};

function Ctrl($window, $scope, $q, $rootScope, jsSdkSettings, jsSdkPaymentProfile, loggerService, $timeout, trackingService, utilsService, modalService) {
  $scope.paymentProvider = initialize();

  let lastSubmittedNonce = null;

  $scope.showPaymentProfilesList = false;

  $scope.showAddPayment = false;
  $scope.paymentProfiles = [];

  _loadPaymentProfiles();

  $scope.$on('login', () => {
    _loadPaymentProfiles();
  });

  $scope.deletePaymentProfile = function(paymentProfile, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }

    modalService.confirmation({
        modalMessage: 'Are you sure you want to delete this card?',
        confirmBtnTxt: 'Yes',
        confirmingBtnTxt: 'Deleting',
        cancelBtnTxt: 'No'
      })
      .then(result => {
        if (result && result.confirm) {
          $q.resolve(paymentProfileService.deleteByIdAndUserId(paymentProfile.id, $rootScope.currentUser.id))
            .then(() => {
              $scope.paymentProfiles = _reject($scope.paymentProfiles, {
                id: paymentProfile.id
              });

              if (paymentProfile.id === $scope.paymentProfile.id) {
                $scope.paymentProfile = null;

                if (!$scope.paymentProfiles.length) {
                  $scope.addProfile();
                } else {
                  _setDefaultPaymentProfile($scope.paymentProfiles[0]);
                }
              } else {
                $scope.setPaymentProfile(null);
              }
            })
            .then(() => {
              $rootScope.$emit('payment-profile-updated');

              result.modalInstance.close();
            })
            .catch(err => {
              $scope.errorMsg = err.message;

              result.modalInstance.close();
            });
        }
      });
  };

  $scope.hideAddPayment = function($event) {
    if ($event) $event.preventDefault();

    $scope.showAddPayment = false;
  };

  function initialize() {
    return jsSdkSettings.paymentConfig().then(resp => {
      const {
        type,
        clientToken
      } = resp;

      let promise;

      switch (type) {
        case PAYMENT_PROVIDER_TYPE.STRIPE:
          promise = _initializeStripe(clientToken);
          break;
        default:
          promise = null;
      }

      return promise.then(client => ({
        client,
        type
      }));
    });
  }

  function _initializeStripe(token) {
    $window.Stripe.setPublishableKey(token);

    return $q(resolve => resolve($window.Stripe.card));
  }

  $scope.addNewCard = function(form, values) {
    $scope.saving = true;

    $scope.submitted = true;

    if (!form.$valid) {
      $scope.saving = false;

      return;
    }

    let providerType;

    $scope.paymentProvider
      .then(provider => {
        providerType = provider.type;
        return _createPaymentToken(provider, values);
      })
      .then(token => {
        return _addPaymentProfile(token, providerType);
      })
      .catch(err => {
        $scope.formErrors = ['Unable to save credit card.'];

        const errs = err.message ? [err] : _get(err, 'details.originalError.fieldErrors["0"].fieldErrors');

        errs.forEach(error => {
          if (error && error.message) {
            $scope.formErrors.push(error.message);
          }
        });

        $scope.saving = false;
      });
  };

  $scope.selectProfile = function(selected) {
    setPaymentProfile(selected);

    _setDefaultPaymentProfile(selected);

    $scope.showPaymentProfilesList = false;
  };

  $scope.addProfile = function() {
    $scope.formErrors = [];
    $scope.showAddPayment = true;
  };

  $scope.showList = function($event) {
    if ($event) $event.preventDefault();

    $scope.showPaymentProfilesList = true;
  };

  function _createPaymentToken(provider, values) {
    switch (provider.type) {
      case PAYMENT_PROVIDER_TYPE.BRAINTREE:
        return _createBraintreePaymentToken(provider.client, values);
      case PAYMENT_PROVIDER_TYPE.STRIPE:
        return _createStripePaymentToken(provider.client, values);
      default:
        return null;
    }
  }

  function _createBraintreePaymentToken(client, values) {
    const creditCard = {
      number: values.ccNumber,
      expirationDate: `${values.ccExpMonth}/${values.ccExpYear}`,
      cvv: values.ccCvc,
      billingAddress: {
        postalCode: values.postalCode
      }
    };

    const options = {
      endpoint: 'payment_methods/credit_cards',
      method: 'post',
      data: {
        creditCard
      }
    };

    return $q((resolve, reject) => {
      client.request(options, function(err, response) {
        if (err) return reject(err);

        const token = _get(response, 'creditCards[0].nonce');

        return resolve(token);
      });
    });
  }

  function _createStripePaymentToken(client, values) {
    return $q((resolve, reject) => {
      const options = {
        number: values.ccNumber,
        cvc: values.ccCvc,
        exp_month: values.ccExpMonth,
        exp_year: values.ccExpYear,
        address_zip: values.postalCode
      };

      client.createToken(options, (status, response) => {
        if (response.error) return reject(response.error);

        return resolve(response.id);
      });
    });
  }

  function setPaymentProfile(profile) {
    $scope.paymentProfile = profile;

    if ($scope.onChange) {
      $scope.onChange(profile);
    }
  }

  function _addPaymentProfile(nonce, providerType) {
    if (nonce === lastSubmittedNonce) {
      $scope.saving = false;

      $scope.formErrors.push('Unable to Save Card. Card already exists.');

      return null;
    }

    lastSubmittedNonce = nonce;

    return jsSdkPaymentProfile.createByNonceToken(nonce, {
        providerType
      })
      .then(resp => {
        return _setDefaultPaymentProfile(resp)
          .then(() => {
            return resp;
          });
      })
      .then(resp => {
        setPaymentProfile(resp);

        $scope.paymentProfiles.push(resp);

        $scope.showAddPayment = false;

        $scope.saving = false;

        const trackingData = {
          cardType: resp.cardType,
          last4: resp.last4,
          paymentProfileId: resp.id
        };

        trackingService.trackEvent(SEGMENT_EVENTS.AddedCreditCard, trackingData);

        if ($scope.callback) {
          $scope.callback(resp);
        }
      })
      .catch(err => {
        const error = utilsService.errorMessage(err) || 'Error processing card';

        $scope.formErrors.push(`${error} - Please try again.`);

        loggerService.error(err);

        $scope.saving = false;
      });
  }

  function _setDefaultPaymentProfile(paymentProfile) {
    $scope.saving = true;

    return jsSdkPaymentProfile.setDefault(paymentProfile.id)
      .then(() => {
        $scope.saving = false;

        $scope.paymentProfile = paymentProfile;

        $rootScope.$emit('payment-profile-updated');
      })
      .catch(resp => {
        const msg = utilsService.errorMessage(resp);

        $scope.formErrors.push(msg);

        loggerService.error(resp);

        $scope.saving = false;
      });
  }

  function _loadPaymentProfiles() {
    $scope.loading = true;

    if (!$rootScope.isLoggedIn()) {
      $scope.loading = false;

      return;
    }

    $scope.paymentProvider
      .then(provider => jsSdkPaymentProfile.list({
        providerType: provider.type
      }))
      .then(resp => {
        $scope.loading = false;

        if (resp && resp.length) {
          setPaymentProfile(findDefaultPaymentProfile(resp));

          $scope.paymentProfiles = _sortBy(resp, function(profile) {
              return profile.modified;
            })
            .reverse()
            .slice(0, 4);
        } else {
          $scope.addProfile();
        }
      })
      .catch(() => {
        $scope.formErrors.push('Error loading Credit Card information. Please enter updated information.');

        $scope.loading = false;
      });
  }

  function findDefaultPaymentProfile(paymentProfiles) {
    const profile = _find(paymentProfiles, {
      default: true
    });

    return profile || _last(paymentProfiles);
  }
}

Ctrl.$inject = [
  '$window',
  '$scope',
  '$q',
  '$rootScope',
  'jsSdk.settings',
  'jsSdk.paymentProfile',
  'loggerService',
  '$timeout',
  'trackingService',
  'utilsService',
  'modalService'
];

export default Ctrl;



// WEBPACK FOOTER //
// ./src/directives/paymentProfile/controller.js